/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2017, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../lib/@waca/core-client/js/core-client/ui/core/Class',
	'../../lib/@waca/core-client/js/core-client/utils/ClassFactory',
	'../../lib/@waca/core-client/js/core-client/i18n/Formatter',
	'./FilterDockItemView',
	'../../widgets/livewidget/nls/StringResources',
	'jquery',
	'underscore',
	'../FilterMetadataHelper',
	'../finder/FilterModuleFinder',
	'../../util/DashboardFormatter'
],
function(Class, AMD, Formatter, FilterDockItemView, resources, $, _,
	FilterMetadataHelper, FilterModuleFinder, DashboardFormatter) {

	var FilterDockItemController = Class.extend({

		_AMD: AMD,

		/**
			 * Constructor
			 *
			 * @param {object} params - arguments
			 *
			 * @constructs FilterDockItemController
			 */
		init: function(params) {
			FilterDockItemController.inherited('init', this, arguments);

			this.dashboardApi = params.dashboardApi;
			this.pageContextAPI = params.pageContextAPI;
			this.scope = params.scope;
			this.item = params.item;
			this.onDelete = params.onDelete;
			this.dockKey = params.dockKey;
			this.getFilterItem = params.getFilterItem;
			this.ajaxSvc = params.ajaxSvc;
			this.parentLayoutId = params.parentLayoutId;
			this.singleDockMode = params.singleDockMode;
			this.excludeAddToCanvas = params.excludeAddToCanvas;
			this.id = _.uniqueId('FilterDockItemController_');
			this.dataSources = params.dataSources;
			//listen for browser window resize, adjust mountNode as needed
			this.windowResizeHandler = this._onWindowResize.bind(this);
			$(window).on('resize', this.windowResizeHandler);

			this.registerEvents();

			let filterOptions = {
				menuActionHandler: this.menuActionHandler.bind(this),
				getFilterController: this.getFilterController.bind(this),
				isAuthoringMode: this.dashboardApi.getMode() === 'authoring',
				iconsFeature: this.dashboardApi.getFeature('Icons')
			};
			this.ready = this._whenMetadataReady()
				.then(() => {
					if (this.item) { // Ensure item exists. Remove might have been invoked while the async function runs
						_.extend(filterOptions, {
							onOpenDropdownFilterView: this.openDropdownFilterView.bind(this),
							isItemAHierarchy: this.item.isHierarchy()
						});
					}
				}).catch(e => {
					this.excludeAddToCanvas = true;
					_.extend(filterOptions, {
						onOpenDropdownFilterView: this.openDropdownErrorFilterView.bind(this, e),
						missingData: true
					});
				}).finally(() => {
					this.view = new FilterDockItemView(_.extend(filterOptions, {
						viewModel: this._getViewModel()
					}));
					return this.registerSourceUsage();
				});

		},

		_onWindowResize: function() {
			if (this.view && this.filterInstance && this.filterInstance.isOpen()) {
				this.view.adjustMountNodeHeight(this.filterInstance.MIN_HEIGHT, this.filterInstance.HEIGHT);
			}
		},

		_getItemMetadataLabel: function () {
			const dataSources = this.dashboardApi.getFeature('DataSources');
			const sourceId = this.item.getSourceId();
			const itemId = this.item.getItemId();
			const metadata = dataSources.getDataSource(sourceId).getMetadataColumn(itemId);
			return metadata && metadata.getLabel();
		},

		_getViewModel: function() {
			return {
				name: this._getItemMetadataLabel() || this.item.getItemName(),
				dockKey: this.dockKey,
				text: this._getFilterText() || this.generateAllMembersString(),
				isRange: this.item.isRange(),
				isNamedSet: this.item.getIsNamedSet(),
				selectedCount: this._getSelectCount(),
				menuItems: this._getMenuItems(),
				menuIcon: 'wa-glass_menu',
				error: this.item.getError(),
				overflowMenuAriaLabel: resources.get('filterOverflowMenu'),
				isModeledFilter: this.item.getIsModeledFilter()
			};
		},

		/**
			 * Register events
			 */
		registerEvents: function() {
			this.dashboardApi.on('card:error', this.onCardError, this);
			this.dashboardApi.on('pagecontext:filterContextUpdated', this.onFilterContextUpdated, this);
			this.dashboardApi.on('filterDock:showItemMenu', this.showItemMenu, this);
			this.dashboardApi.on('filterDock:hideItemMenu', this.hideItemMenu, this);
		},

		/**
			 * Unregister events
			 */
		unregisterEvents: function() {
			this.dashboardApi.off('card:error', this.onCardError, this);
			this.dashboardApi.off('pagecontext:filterContextUpdated', this.onFilterContextUpdated, this);
			this.dashboardApi.off('filterDock:hideItemMenu', this.showItemMenu, this);
			this.dashboardApi.off('filterDock:hideItemMenu', this.hideItemMenu, this);
		},

		registerSourceUsage: function() {
			const dataSourceSvc =  this.dashboardApi.getFeature('dataSources.deprecated');
			this.sourceCollection = dataSourceSvc.getSourcesCollection();
			this.sourceCollection.registerUsage(this.id, this.item.getSourceId());
		},

		unregisterSourceUsage: function() {
			if (this.sourceCollection) {
				this.sourceCollection.deregisterUsage(this.id);
			}
		},

		/**
			 *
			 * @return {Array} Array of actions for the filter item menu
			 */
		_getMenuItems: function() {
			var actions = [];
			// do not add canvas action to menu when excludeAddToCanvas flag is set to true
			if (!this.excludeAddToCanvas && !this.item.getIsModeledFilter() && (this.singleDockMode || this.item.getScope() !== 'global')) {
				actions.push({
					'label': resources.get('filterItemCanvasMove'),
					'action': 'filter-menu-add-to-canvas-action'
				});
			}
			actions.push({
				'label': resources.get('filterItemDelete'),
				'action': 'filter-menu-delete-action'
			});

			return actions;
		},

		/**
			 * @return {String} Comma separated value of selections on the filter item
			 */
		generateSelectedValueString: function() {
			var str;

			if (this.item.isRange()) {
				var from = this.item.getFrom();
				var to = this.item.getTo();
				if (from === '' && to === '') {
					str = '';
				} else {
					var info = this.item.getMetadataInfo();
					if (info && info.maximumFractionDigits !== undefined) {
						str = this._getLabel(from, to, info.maximumFractionDigits);
					} else {
						str = this._getLabel(from, to);
					}

				}
			} else if (this.item.isValueDataItem()) {
				// When an item is based the entire named set, there is no filter value.
				str = resources.get('filter_allMembersOfSet');
			} else {
				str = this._generateMemberString();
			}

			return str;
		},

		generateAllMembersString: function() {
			return this.item.getIsNamedSet() ? resources.get('filter_allMembersOfSet') : '';
		},

		/**
			 * Generates and return docker filter item labelFromTo
			 *
			 * @param {string} from - the from string
			 * @param {string} to - the to string
			 * @param {int} maximumFractionDigits - optional maximumFractionDigits
			 *
			 * @return {string} the label string
			 */
		_getLabel: function(from, to, maximumFractionDigits) {
			var str;
			var dataType = this.item.getDataType();
			if (FilterDockItemController.isDateDataType(dataType)) {
				var result = this._formatDateTime(dataType, from, to);
				str = this._generateDateTimeLabel(result, from, to, this.item.getInvert());
			}

			//Fallback to current default format if not date dataType or failed to localized format date dataType
			if (!str) {
				var spec = {
					type: dataType
				};
				if (maximumFractionDigits !== undefined) {
					spec.maximumFractionDigits = maximumFractionDigits;
				}
				from = Formatter.format(from, spec);
				to = Formatter.format(to, spec);
				str = resources.get(this.item.getInvert() ? 'filterInvertRange' : 'filterRange', {
					from: from,
					to: to
				});
			}
			return str.length ? str : '';
		},

		/**
			 * Generates and return the label string for date time data type
			 *
			 * @param {object} context - the context use to generate the label
			 * @param {string} from - the from string
			 * @param {string} to - the to string
			 * @param {boolean} isInvert - invert boolean flag
			 *
			 * @return {string} the label string
			 */
		_generateDateTimeLabel: function(context, from, to, isInvert) {
			var label;
			if (context) {
				from = context.from || from;
				to = context.to || to;
				if (this._dateIsAfter(from, to)) {
					label = resources.get(isInvert ? 'filter_date_after_except' : 'filter_date_after', {
						from: from
					});
				} else if (this._dateIsBefore(from, to)) {
					label = resources.get(isInvert ? 'filter_date_before_except' : 'filter_date_before', {
						to: to
					});
				} else if (this._dateIsBetween(from, to)) {
					label = resources.get(isInvert ? 'filter_date_between_except' : 'filter_date_between', {
						from: from,
						to: to
					});
				}
			}
			return label;
		},

		/**
			 * Returns true for 'Date is after' else return false
			 *
			 * @param {string} from - the from string
			 * @param {string} to - the to string
			 *
			 * @return {boolean} return true for 'Date is after' else return false
			 */
		_dateIsAfter: function(from, to) {
			return (from && !!from.length) && (!to || !to.length) ? true : false;
		},

		/**
			 * Returns true for 'Date is before' else return false
			 *
			 * @param {string} from - the from string
			 * @param {string} to - the to string
			 *
			 * @return {boolean} return true for 'Date is before' else return false
			 */
		_dateIsBefore: function(from, to) {
			return (to && !!to.length) && (!from || !from.length) ? true : false;
		},

		/**
			 * Returns true for 'Date is between' else return false
			 *
			 * @param {string} from - the from string
			 * @param {string} to - the to string
			 *
			 * @return {boolean} return true for 'Date is between' else return false
			 */
		_dateIsBetween: function(from, to) {
			return (from && !!from.length) && (to && !!to.length) ? true : false;
		},

		_formatDateTime: function(dataType, from, to /*, tupleSet*/ ) {
			var result;
			if (!!from.length || !!to.length) {
				result = {
					from: '',
					to: ''
				};
				// var Utilities = I18NUtils.getI18NUtils();
				// result.from = from; /*this._localizedDateTime(from, dataType, Utilities);*/
				// result.to = to; /*this._localizedDateTime(to, dataType, Utilities);*/

				if (from.length) {
					result.from = Formatter.format(from, {
						type: dataType
					});
				}

				if (to.length) {
					result.to = Formatter.format(to, {
						type: dataType
					});
				}
			}
			return result;
		},

		_generateMemberString: function() {
			var str;
			var selectedContextItems = [];
			_.each(this.item.getValueCaptions(), function(caption) {
				selectedContextItems.push(caption || DashboardFormatter.format(null));
			});

			str = selectedContextItems.join(resources.get('filterValueSeparator') + ' ');
			if (this.item.getExclude()) {
				if (this.item.getValueCaptions().length === 0) {
					str = resources.get('filterAll');
				} else {
					str = resources.get('filterExcept', {
						values: str
					});
				}
			}

			return str;
		},

		/**
			 * Handle the filter item menu click action.
			 *
			 * @param {Jquery event} Event containing menu action to handle
			 */
		menuActionHandler: function(event) {
			//TODO Externalize this handler
			if ($(event.currentTarget).hasClass('disabled')) {
				return;
			}

			var action = event.currentTarget.dataset.action;
			if (action === 'filter-menu-delete-action') {
				this._deleteSelf();
			} else if (action === 'filter-menu-add-to-canvas-action') {
				this._addToCanvas();
			}
		},

		/*
			 * The metadata may not be available dependending on when the filter item was
			 * created. Make sure to load metadata if it's not available from pageContextEntry.
			 */
		_whenMetadataReady: function() {
			const dataSources = this.dashboardApi.getFeature('dataSources.deprecated');
			this.dataSources = dataSources;

			var metadata = this.item.getMetadataInfo();
			if (metadata) {
				return new Promise((resolve, reject) => {
					if (metadata.isColumnUnavailable) {
						reject(new Error(resources.get('errMsgFilterItemMissingTitle', {
							itemName: this.item.getItemId()
						})));
					}
					resolve(null);
				});
			}

			return FilterMetadataHelper.getColumnMetadataInfo(dataSources, this.item.getSourceId(), this.item.getItemId())
				.then((metadata) => {
					if (!_.isEmpty(metadata)) {
						this.item.updateMetadata(metadata);
					} else {
						throw new Error(resources.get('errMsgFilterItemMissingTitle', {
							itemName: this.item.getItemId()
						}));
					}
				});


		},

		_addToCanvas: function() {
			const transactionToken = this.dashboardApi.getFeature('Transaction').startTransaction();
			const containerId = this._getAddToCanvasParentId();
			const content = {
				spec: {
					properties: this._getItemContextProperties(),
					type: 'filter'
				},
				containerId: containerId
			};
			const templateDropZones = this.dashboardApi.getCanvas().getContent(containerId).findContent({ type: 'templateDropZone' });
			if (templateDropZones.length === 0){
				content.layout = {
					height: (['string', 'date', 'datetime', 'time', 'timestamp'].indexOf(this.item.getDataType().toLowerCase()) !== -1) ? '532px' : '382px',
					width: '350px'
				};
			}
			this.dashboardApi.getCanvas().addContent(
				content, transactionToken
			);
		},

		_getAddToCanvasParentId: function() {
			var parentId = this.item.getScope();

			if (this.singleDockMode) {
				parentId = this.parentLayoutId;
			}
			return parentId;
		},

		_deleteItemFromPageContext: function() {
			var beforeItem;
			var nextSibling = this.view.$el.next();
			if (nextSibling.length > 0) {
				beforeItem = this.getFilterItem($(nextSibling).data('dockkey'));
			}

			this.pageContextAPI.updateFilterContext({
				item: this.item,
				scope: this.scope,
				origin: 'filter',
				eventSourceId: this.filterInstance ? this.filterInstance.id : null
			}, {
				command: 'delete'
			}, {
				beforeItem: beforeItem
			});
		},

		_deleteSelf: function() {
			this.dashboardApi.triggerDashboardEvent('filterItem:filterItemBlur');
			this._deleteItemFromPageContext();
			this.onDelete(this);
		},

		_clearAllSelection: function() {
			this.pageContextAPI.updateFilterContext({
				item: this.item,
				scope: this.scope
			}, {
				command: 'clearValues'
			});
		},

		_invertSelection: function() {

			this.getFilterController()
				.then(function(filterController) {
					var cmd = {
						command: 'update'
					};
					if (this.item.isRange()) {
						cmd.condition = {
							from: this.item.getFrom(),
							to: this.item.getTo(),
							invert: !this.item.getInvert()
						};
					} else {
						// For non range filter items we want to clear values instead of doing
						// an update if all members are selected.
						var selectStat = filterController._getSelectStat();
						cmd = selectStat.selected === selectStat.totalMembers ? {
							command: 'clearValues'
						} : cmd;
						// For member filter invert, we do not want to supply all unselected items in tupleSet.
						// Update 'exclude' property accordingly instead.
						cmd.exclude = !this.item.getExclude();
					}
					this.pageContextAPI.updateFilterContext({
						item: this.item,
						scope: this.scope
					}, cmd);

					this.view.updateMenuState(this._getViewModel());
				}.bind(this));
		},

		onFilterContextUpdated: function(payload) {
			payload = payload || {};
			if(payload.ignorePageContextChanged && payload.ignorePageContextChanged()) {
				return;
			}

			if (this.view) {
				this.view.updateMenuState(this._getViewModel());
			}
		},

		_getSelectCount: function() {
			// When invert is on, ideally we want to show distCount-valueCount.
			// However distCount is from MetadataApi, and there is a performance impact to expose it.
			// For now we are showing '-' as Not Applicable.
			return this.item.getExclude() ? '-' : this.item.getValueCount();
		},

		openDropdownErrorFilterView: function(error) {
			this.view.initializeMountNodeForDropDownFilterView();
			this.view.processError(error);
		},

		/**
			 * Opens the filter item view (such as the member picker)
			 */
		openDropdownFilterView: function () {
			this.view.initializeMountNodeForDropDownFilterView();
			let isFilterViewLoaded = this.view.isFilterViewLoaded();
			return this.getFilterController().then(() => {
				this.view.adjustMountNodeHeight(this.filterInstance.MIN_HEIGHT, this.filterInstance.HEIGHT);
				return !isFilterViewLoaded ? this.filterInstance.render() : this.filterInstance.onOpen();
			}).catch(this.view.processError.bind((this.view)));
		},

		/**
			 * Set the filter controller instance and return it
			 * @returns filter controller instance.
			 */
		getFilterController: function() {
			if (this.filterInstance) {
				return this.filterInstance.ready.then(function() {
					return Promise.resolve(this.filterInstance);
				}.bind(this))
					.catch(function(err) {
						return Promise.reject(err);
					});
			}
			const isRange = this.item.isRange();
			const finderOpts = _.extend({ origin: 'filterDock', isRange }, this.item.getMetadataInfo());

			return FilterModuleFinder.getModule(finderOpts)
				.then(function(module) {
					return this._initializeFilterController(module);
				}.bind(this));
		},

		_initializeFilterController: function(module) {
			return this._AMD.loadModule(module).then(function(module) {
				var FilterController = module;
				var params = this._getFilterControllerParams();

				// we need to make 100% sure that when we set this.filterInstance that the instance is ready to use since we will never get back here.
				let filterInstance = new FilterController(params.options, params.extraOptions);
				filterInstance.viewConfig.showHeader = false;
				return filterInstance.initialize().then(function() {
					this.filterInstance = filterInstance;
					return Promise.resolve(this.filterInstance);
				}.bind(this));
			}.bind(this)).catch(function (err) {
				if (this.filterInstance) {
					this.filterInstance.remove();
					this.filterInstance = null;
				}
				return this.view.processError(err);
			}.bind(this));
		},

		_getFilterControllerParams: function() {
			var params = {};

			params.options = {
				dashboardApi: this.dashboardApi,
				pageContextAPI: this.pageContextAPI,
				containerPageId: this.scope,
				initialConfigJSON: {
					properties: this._getItemContextProperties()
				},
				ajaxSvc: this.ajaxSvc,
				hasSearchIcon: true
			};

			params.extraOptions = {
				commonView: this.view
			};

			return params;

		},

		_getItemContextProperties: function(){
			return {
				itemName: this.item.getItemName(),
				itemId: this.item.getItemId(),
				sourceId: this.item.getSourceId(),
				dataType: this.item.getDataType(),
				isNamedSet: this.item.getIsNamedSet(),
				usage: this.item.getUsage(),
				isHierarchy: this.item.isHierarchy(),
				scope: this.item.getScope()
			};
		},

		toggleCardHighlighting: function(display) {
			//if display is false but a filter menu is open somewhere, we still want to have the highlight happen
			if (display || this.view.isAnyFilterViewOpen()) {
				this.dashboardApi.triggerDashboardEvent('filterItem:filterItemFocus', {
					itemKey: this.item.getKey()
				});
			} else {
				this.dashboardApi.triggerDashboardEvent('filterItem:filterItemBlur');
			}
		},

		/**
			 * update the view. This function is invoked when there is
			 * a change in the page context item
			 */
		update: function() {
			if (this.view) {
				const _viewModel = {
					name: this._getItemMetadataLabel() || this.item.getItemName(),
					text: this._getFilterText(),
					selectedCount: this._getSelectCount(),
					isModeledFilter: this.item.getIsModeledFilter(),
					error: this.item.getError()
				};
				this.view.update(_viewModel);
			}
		},

		_getFilterText(){
			return this.item.getIsModeledFilter() ? resources.get('modelFilter'): this.generateSelectedValueString();
		},

		getItemDOMnode: function() {
			return this.view.$el;
		},

		remove: function() {
			this.unregisterEvents();
			this.unregisterSourceUsage();

			if (this.filterInstance) {
				this.filterInstance.remove();
				this.filterInstance = null;
			}

			if (this.windowResizeHandler) {
				$(window).off('resize', this.windowResizeHandler);
			}

			if (this.view) {
				this.view.remove();
				this.view = null;
			}

			this.pageContextAPI = null;
			this.scope = null;
			this.item = null;
		},

		/**
			 * Card error handler
			 *
			 * @param {object} payload - payload
			 */
		onCardError: function(payload) {
			if (!payload || !payload.error || !this.item) {
				return;
			}

			var assetId = this.item.getAssetId();
			var error = payload.error;

			if (error.tableId === assetId) {
				this.item.setError(error);
				this.view.toggleWarningIcon(error.message, true);
			}
		},

		showItemMenu: function() {
			if (this.view && this.view.showItemMenu) {
				this.view.showItemMenu();
			}
		},

		hideItemMenu: function() {
			if (this.view && this.view.hideItemMenu) {
				this.view.hideItemMenu();
			}
		}

	});

		//Static method
		/**
		 *  Generates a key based on item id and asset id of a page context item
		 * @return key in the format 'itemId/assetId'
		 *
		 */
	FilterDockItemController.genFilterItemDockKey = function(item) {
		return item.getKey();
	};

	/**
		 * Returns a boolean indicating whether the dataType is a date/time data type or not
		 *
		 * @return {boolean} true if dataType is a date/time data type else return false
		 */
	FilterDockItemController.isDateDataType = function(type) {
		return (type === 'date' || type === 'time' || type === 'datetime' || type === 'timestamp');
	};

	return FilterDockItemController;
});
